import { inputGradient, syncPaintInput } from "./native-paint-input";
import type { GradientPaint } from "../native/gradient";
import { changePaint } from "../native/paint-command";
import { appearanceValue, changeAppearance } from "../native/appearance";
import { NativePenSettings } from "./native-pen-settings";
import {
  changePenSmoothing,
  rememberPen,
  supportsPenSmoothing,
} from "../native/pen-smoothing";
import {
  originalCompact,
  syncOriginalCompact,
  wrapOriginalSection,
} from "./native-original-controls";
import type { NativeSession } from "../native/session";
import type { NativePointer } from "../native/pointer";
import type { Point, SceneNode } from "../scene/model";
import { documentPoint, hitNode, type Camera } from "../native/geometry";
import {
  pathNode,
  reframePath,
  vectorPath,
  type PathAnchor,
} from "../native/vector-path";
import { canClosePen, penAnchors, preparePenPoints } from "../native/pen-path";
import { nodeMatrix } from "../native/transforms";
import { transformPoint, invertAffine } from "../geometry/affine";
import { paintVector } from "../render/native-vector-path";
import type { WorkTool } from "./native-tool-groups";
export class NativePaths {
  tool: "line" | "arrow" | "pen" | "bezier" | null = null;
  private penSettings: NativePenSettings;
  private penPreviewPending = false;
  private anchors: PathAnchor[] = [];
  private preview: SceneNode | null = null;
  private edit: string | null = null;
  private closing = false;
  private gesture: {
    id: number;
    start: Point;
    points: Point[];
    smoothing?: number;
    anchor?: number;
    part?: "incoming" | "outgoing";
    before?: SceneNode;
  } | null = null;
  private panel = document.getElementById("drawing-properties")!;
  private defaults = {
    stroke: "#172033",
    strokeAlpha: 1,
    strokeGradient: null as GradientPaint | null,
    strokeWidth: 8,
    lineStyle: "solid",
  };
  private defaultsStart: typeof this.defaults | null = null;
  private styleStart: SceneNode[] | null = null;
  constructor(
    private session: NativeSession,
    private pointer: NativePointer,
    private overlay: HTMLCanvasElement,
    private camera: Camera,
    private changed: () => void,
    private select: () => void,
    private blocked: () => boolean,
  ) {
    for (const [id, key] of [
      ["drawing-color", "stroke"],
      ["drawing-size", "strokeWidth"],
      ["drawing-line-style", "lineStyle"],
    ] as const) {
      const field = document.getElementById(id) as HTMLInputElement;
      field.oninput = () => {
        const value = key === "strokeWidth" ? Number(field.value) : field.value;
        if (!this.defaultsStart) this.defaultsStart = this.defaults;
        const channel = field.dataset.colorChannel;
        this.defaults = {
          ...this.defaults,
          ...(channel === "gradient"
            ? { strokeGradient: inputGradient(field) }
            : channel === "alpha"
              ? { strokeAlpha: Number(field.dataset.colorAlpha ?? 1) }
              : {
                  [key]: value,
                  ...(key === "stroke" ? { strokeGradient: null } : {}),
                }),
        };
        if (!this.styleStart) {
          this.styleStart = this.session.selectedNodes.filter(
            (n) => n.kind === "path" && !n.geometry.svgPath,
          );
          this.session.begin();
        }
        this.session.replaceMany(
          key === "stroke" &&
            (channel === "color" ||
              channel === "alpha" ||
              channel === "gradient")
            ? changePaint(
                this.styleStart,
                "stroke",
                field.value,
                Number(field.dataset.colorAlpha ?? 1),
                channel,
                inputGradient(field),
              )
            : changeAppearance(this.styleStart, key, value),
        );
        this.changed();
      };
      field.onchange = () => {
        this.session.finish();
        this.styleStart = null;
        this.defaultsStart = null;
        this.changed();
      };
      field.onkeydown = (e) => {
        if (e.key === "Escape" && this.styleStart) {
          e.preventDefault();
          e.stopPropagation();
          this.session.cancel();
          this.styleStart = null;
          if (this.defaultsStart) this.defaults = this.defaultsStart;
          this.defaultsStart = null;
          field.blur();
          this.changed();
        }
      };
    }
    wrapOriginalSection("drawing-properties", [
      {
        title: "Внешний вид",
        icon: "color",
        selectors: [
          "#drawing-color-row",
          ".property-range-row",
          "#drawing-size",
          "label[for=drawing-line-style]",
          "#drawing-smoothing-row",
          "#drawing-properties-hint",
        ],
      },
    ]);
    originalCompact("native-path-compact", [
      { id: "drawing-color", icon: "color", title: "Цвет линии" },
      { id: "drawing-size", icon: "stroke-width", title: "Толщина линии" },
      { id: "drawing-line-style", icon: "dashed", title: "Стиль линии" },
      {
        id: "drawing-smoothing",
        icon: "smoothing",
        title: "Сглаживание новых линий",
      },
    ]);
    this.penSettings = new NativePenSettings(changed);
    overlay.addEventListener("lostpointercapture", (event) => {
      if (this.gesture?.id === event.pointerId) this.cancel();
    });
    overlay.addEventListener(
      "dblclick",
      (e) => {
        if (this.blocked()) return;
        if (this.tool === "bezier") {
          this.finishBezier();
          e.preventDefault();
          return;
        }
        const node = hitNode(
          session.page.nodes,
          documentPoint({ x: e.clientX, y: e.clientY }, camera),
          camera.zoom,
        );
        if (
          node?.kind === "path" &&
          !node.geometry.svgPath &&
          !node.geometry.warpCorners
        ) {
          this.select();
          this.edit = node.id;
          session.select([node.id]);
          this.changed();
        }
      },
      true,
    );
  }
  activate(tool: WorkTool | null) {
    this.cancel();
    this.tool =
      tool && ["line", "arrow", "pen", "bezier"].includes(tool)
        ? (tool as typeof this.tool)
        : null;
    this.edit = null;
    this.changed();
  }
  get active() {
    return !!this.gesture;
  }
  private make(anchors: PathAnchor[], closed = false) {
    const n = pathNode(
      crypto.randomUUID(),
      anchors,
      closed,
      this.tool === "arrow",
    );
    n.appearance = { ...n.appearance, ...this.defaults };
    n.layer.name =
      this.tool === "pen"
        ? "Ручка"
        : this.tool === "bezier"
          ? "Перо Безье"
          : this.tool === "arrow"
            ? "Стрелка"
            : "Линия";
    return this.tool === "pen"
      ? changePenSmoothing(
          [rememberPen(n)],
          this.gesture?.smoothing ?? this.penSettings.amount,
        )[0]!
      : n;
  }
  private finishBezier(closed = false) {
    if (this.anchors.length >= 2) {
      const n = this.make(this.anchors, closed);
      this.session.replaceNodes([...this.session.page.nodes, n]);
      this.session.select(closed ? [n.id] : []);
      this.session.finish();
    }
    this.anchors = [];
    this.preview = null;
    this.gesture = null;
    this.penPreviewPending = false;
    this.closing = false;
    this.changed();
  }
  cancel() {
    if (this.styleStart) this.session.cancel();
    this.styleStart = null;
    if (this.defaultsStart) this.defaults = this.defaultsStart;
    this.defaultsStart = null;
    const gesture = this.gesture;
    if (gesture?.before) this.session.cancel();
    this.gesture = null;
    this.penPreviewPending = false;
    this.closing = false;
    if (gesture && this.overlay.hasPointerCapture(gesture.id))
      this.overlay.releasePointerCapture(gesture.id);
    this.preview = null;
    this.anchors = [];
    this.changed();
  }
  event(e: PointerEvent) {
    if (
      this.blocked() ||
      this.pointer.space ||
      e.button === 1 ||
      ((e.ctrlKey || e.metaKey) && !this.gesture)
    )
      return false;
    const p = documentPoint({ x: e.clientX, y: e.clientY }, this.camera),
      edit = this.session.page.nodes.find(
        (n) => n.id === this.edit && this.session.selectedIds.includes(n.id),
      );
    if (e.type === "pointerdown" && edit) {
      const m = nodeMatrix(edit),
        a = vectorPath(edit).anchors;
      let match: { anchor: number; part?: "incoming" | "outgoing" } | null =
        null;
      for (let i = 0; i < a.length; i++)
        for (const part of [undefined, "incoming", "outgoing"] as const) {
          const q = part ? a[i]![part] : a[i];
          if (q) {
            const world = transformPoint(q, m);
            if (
              Math.hypot(world.x - p.x, world.y - p.y) <
              (e.pointerType === "touch" ? 18 : 9) / this.camera.zoom
            )
              match = { anchor: i, ...(part ? { part } : {}) };
          }
        }
      if (match) {
        this.session.begin();
        this.gesture = {
          id: e.pointerId,
          start: p,
          points: [],
          before: edit,
          ...match,
        };
        this.overlay.setPointerCapture(e.pointerId);
        return true;
      }
      this.edit = null;
      this.changed();
      if (!this.tool) return false;
    }
    if (!this.tool && !this.gesture) return false;
    if (e.type === "pointerdown" && e.button === 0) {
      this.overlay.focus();
      if (this.tool === "bezier") {
        if (
          this.anchors.length > 2 &&
          Math.hypot(p.x - this.anchors[0]!.x, p.y - this.anchors[0]!.y) <
            8 / this.camera.zoom
        ) {
          this.finishBezier(true);
          return true;
        }
        if (
          !this.anchors.length ||
          Math.hypot(
            p.x - this.anchors.at(-1)!.x,
            p.y - this.anchors.at(-1)!.y,
          ) >
            2 / this.camera.zoom
        )
          this.anchors.push(p);
      }
      this.gesture = {
        id: e.pointerId,
        start: p,
        points: [p],
        smoothing: this.penSettings.amount,
      };
      this.overlay.setPointerCapture(e.pointerId);
      this.changed();
      return true;
    }
    const g = this.gesture;
    if (!g && this.tool === "bezier" && e.type === "pointermove") {
      const closing =
        this.anchors.length > 2 &&
        Math.hypot(p.x - this.anchors[0]!.x, p.y - this.anchors[0]!.y) <
          8 / this.camera.zoom;
      if (closing !== this.closing) {
        this.closing = closing;
        this.changed();
      }
    }
    if (!g || g.id !== e.pointerId) return !!this.tool;
    if (e.type === "pointercancel") {
      this.cancel();
      return true;
    }
    if (e.type === "pointermove" || e.type === "pointerup") {
      if (g.before) {
        const inv = invertAffine(nodeMatrix(g.before))!;
        const local = transformPoint(p, inv),
          v = structuredClone(vectorPath(g.before)),
          a = v.anchors[g.anchor!]!;
        if (g.part) a[g.part] = local;
        else {
          const dx = local.x - a.x,
            dy = local.y - a.y;
          a.x = local.x;
          a.y = local.y;
          for (const key of ["incoming", "outgoing"] as const)
            if (a[key]) a[key] = { x: a[key]!.x + dx, y: a[key]!.y + dy };
        }
        this.session.replaceNode({
          ...g.before,
          geometry: { ...g.before.geometry, vector: v },
        });
      } else if (this.tool === "bezier") {
        const a = this.anchors.at(-1)!;
        if (
          Math.hypot(p.x - g.start.x, p.y - g.start.y) >
          2 / this.camera.zoom
        ) {
          a.outgoing = p;
          a.incoming = { x: 2 * a.x - p.x, y: 2 * a.y - p.y };
        }
        if (this.anchors.length > 1) this.preview = this.make(this.anchors);
      } else if (this.tool === "pen") {
        if (
          Math.hypot(p.x - g.points.at(-1)!.x, p.y - g.points.at(-1)!.y) >
          1 / this.camera.zoom
        )
          g.points.push(p);
        if (
          e.type === "pointerup" &&
          Math.hypot(p.x - g.points.at(-1)!.x, p.y - g.points.at(-1)!.y) > 0.001
        )
          g.points.push(p);
        this.closing = canClosePen(
          g.points,
          p,
          this.camera.zoom,
          e.pointerType === "touch",
        );
        this.penPreviewPending = true;
        if (e.type === "pointerup") this.updatePenPreview(true);
      } else {
        const end = { ...p };
        if (e.shiftKey) {
          if (Math.abs(p.x - g.start.x) >= Math.abs(p.y - g.start.y))
            end.y = g.start.y;
          else end.x = g.start.x;
        }
        this.preview = this.make([g.start, end]);
      }
    }
    if (e.type === "pointerup") {
      this.gesture = null;
      this.closing = false;
      if (g.before) {
        const node = this.session.page.nodes.find((n) => n.id === g.before!.id);
        if (node) {
          const reframed = reframePath(node);
          // Manual anchor edits establish a new baseline, never resurrect stale points.
          this.session.replaceNode(
            supportsPenSmoothing(node) ? rememberPen(reframed) : reframed,
          );
        }
        this.session.finish();
      } else if (this.tool !== "bezier") {
        if (
          this.preview &&
          (this.tool === "pen"
            ? g.points.some(
                (q) =>
                  Math.hypot(q.x - g.start.x, q.y - g.start.y) *
                    this.camera.zoom >=
                  3,
              )
            : Math.hypot(p.x - g.start.x, p.y - g.start.y) * this.camera.zoom >=
              3)
        ) {
          this.session.replaceNodes([...this.session.page.nodes, this.preview]);
          this.session.select([this.preview.id]);
          this.session.finish();
        }
        this.preview = null;
      }
      if (this.overlay.hasPointerCapture(e.pointerId))
        this.overlay.releasePointerCapture(e.pointerId);
    }
    this.changed();
    return true;
  }
  private updatePenPreview(final = false) {
    const g = this.gesture;
    if (!g || this.tool !== "pen" || !this.penPreviewPending) return;
    this.penPreviewPending = false;
    if (g.points.length < 2) return;
    const points = this.closing
      ? [...g.points.slice(0, -1), g.start]
      : g.points;
    // Positive presets use the same curve while drawing and on release. Work runs
    // at most once per paint, not once for every delivered pointer sample.
    const prepared =
      final || g.smoothing
        ? preparePenPoints(points, this.defaults.strokeWidth, this.camera.zoom)
        : points;
    const anchors = penAnchors(prepared, this.closing);
    this.preview =
      anchors.length > 1
        ? this.make(anchors, this.closing && anchors.length > 2)
        : null;
  }
  key(e: KeyboardEvent) {
    if (
      (e.target as HTMLElement).closest(
        "input,textarea,select,[contenteditable=true]",
      )
    )
      return false;
    if (e.key === "Escape" && (this.tool || this.edit)) {
      if (this.gesture || this.anchors.length) this.cancel();
      else {
        this.edit = null;
        this.select();
      }
      return true;
    }
    if (e.key === "Enter" && this.tool === "bezier") {
      this.finishBezier();
      return true;
    }
    return false;
  }
  render(ctx: CanvasRenderingContext2D) {
    this.updatePenPreview();
    this.penSettings.render(
      this.tool === "pen" && !this.session.selectedNodes.length,
    );
    const selected = this.session.selectedNodes.filter(
        (n) => n.kind === "path" && !n.geometry.svgPath,
      ),
      show = !!this.tool || selected.length > 0;
    // Closed paths use the shared appearance section, with fill and stroke together.
    this.panel.hidden =
      !show || (!this.active && selected.some((n) => vectorPath(n).closed));
    document.getElementById("native-path-compact")!.hidden = !show;
    document.getElementById("drawing-properties-title")!.textContent =
      this.tool === "bezier"
        ? "Перо Безье"
        : this.tool === "arrow"
          ? "Стрелка"
          : this.tool === "line"
            ? "Линия"
            : "Ручка";
    for (const [id, key] of [
      ["drawing-color", "stroke"],
      ["drawing-size", "strokeWidth"],
      ["drawing-line-style", "lineStyle"],
    ] as const) {
      const field = document.getElementById(id) as HTMLInputElement;
      if (document.activeElement !== field)
        field.value = String(
          selected[0] ? appearanceValue(selected[0], key) : this.defaults[key],
        );
    }
    (
      document.getElementById("drawing-color") as HTMLInputElement
    ).dataset.displayAlpha = String(
      selected[0]?.appearance.strokeAlpha ?? this.defaults.strokeAlpha,
    );
    syncPaintInput(
      document.getElementById("drawing-color") as HTMLInputElement,
      selected[0]
        ? selected[0].appearance.strokeGradient
        : this.defaults.strokeGradient,
    );
    syncOriginalCompact("native-path-compact");
    document.getElementById("drawing-size-value")!.textContent =
      (document.getElementById("drawing-size") as HTMLInputElement).value +
      " px";
    ctx.save();
    ctx.translate(this.camera.x, this.camera.y);
    ctx.scale(this.camera.zoom, this.camera.zoom);
    if (this.preview) {
      ctx.save();
      ctx.translate(this.preview.transform.x!, this.preview.transform.y!);
      paintVector(ctx, this.preview);
      ctx.restore();
    }
    const edit = selected.find((n) => n.id === this.edit),
      anchors = edit
        ? vectorPath(edit).anchors.map((a) => {
            const m = nodeMatrix(edit);
            return {
              ...transformPoint(a, m),
              ...(a.incoming
                ? { incoming: transformPoint(a.incoming, m) }
                : {}),
              ...(a.outgoing
                ? { outgoing: transformPoint(a.outgoing, m) }
                : {}),
            };
          })
        : this.anchors;
    ctx.strokeStyle = "#5977ed";
    ctx.fillStyle = "#ffffff";
    ctx.lineWidth = 1 / this.camera.zoom;
    for (const a of anchors) {
      for (const key of ["incoming", "outgoing"] as const) {
        const q = a[key];
        if (q) {
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(q.x, q.y);
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(q.x, q.y, 3 / this.camera.zoom, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        }
      }
      const r = 4 / this.camera.zoom;
      ctx.fillRect(a.x - r, a.y - r, 2 * r, 2 * r);
      ctx.strokeRect(a.x - r, a.y - r, 2 * r, 2 * r);
    }
    const start = this.tool === "pen" ? this.gesture?.start : this.anchors[0];
    if (start && this.closing) {
      ctx.beginPath();
      ctx.arc(start.x, start.y, 8 / this.camera.zoom, 0, Math.PI * 2);
      ctx.fillStyle = "#22d3a5";
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 2 / this.camera.zoom;
      ctx.fill();
      ctx.stroke();
    }
    ctx.restore();
  }
}
